import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const LiveAnalyticsContext = createContext(null);

// Web Audio API Synthesizer for high-fidelity notification sound chime
function playNotificationChime(type = "info") {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();

    // Frequency combinations based on notification tone
    let freqs = [523.25, 659.25, 783.99]; // C5, E5, G5 (Bright chord)
    if (type === "announcement" || type === "urgent") {
      freqs = [440, 554.37, 659.25, 880]; // A4, C#5, E5, A5 (Celebration/Alert)
    } else if (type === "warning") {
      freqs = [493.88, 622.25, 739.99]; // B4, D#5, F#5
    }

    const now = ctx.currentTime;
    freqs.forEach((f, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(f, now + idx * 0.08);

      gain.gain.setValueAtTime(0.001, now + idx * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.18, now + idx * 0.08 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.08 + 0.5);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + idx * 0.08);
      osc.stop(now + idx * 0.08 + 0.55);
    });
  } catch (err) {
    // Audio context may be restricted by autoplay policy until user interacts
  }
}

export function LiveAnalyticsProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [adminLiveStats, setAdminLiveStats] = useState(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const adminSubscribersRef = useRef(new Set());

  // Connect to WebSocket server
  const connectWebSocket = useCallback(() => {
    if (socketRef.current && (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    try {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${protocol}//${window.location.host}/ws/live`;
      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);

        // Send initial identify if user is logged in
        if (user) {
          ws.send(JSON.stringify({
            type: "identify",
            user: {
              user_id: user.user_id,
              name: user.name,
              email: user.email,
              role: user.role
            }
          }));
        }

        // Send current page
        ws.send(JSON.stringify({
          type: "page_view",
          path: window.location.pathname,
          title: document.title,
          referrer: document.referrer
        }));

        // If user is admin, auto-subscribe to live stats
        if (user?.role === "admin") {
          ws.send(JSON.stringify({
            type: "admin:subscribe",
            token: "admin_active_session"
          }));
        }

        // Setup ping interval
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === "notification" && msg.payload) {
            const notif = msg.payload;
            // Play chime sound if enabled
            if (notif.playSound) {
              playNotificationChime(notif.type);
            }

            setNotifications((prev) => {
              // Avoid duplicate notification IDs
              if (prev.some((n) => n.id === notif.id)) return prev;
              return [notif, ...prev];
            });

            // Auto-dismiss after duration
            if (notif.duration && notif.duration > 0) {
              setTimeout(() => {
                dismissNotification(notif.id);
              }, notif.duration);
            }
          } else if (msg.type === "admin:live-stats" && msg.data) {
            setAdminLiveStats(msg.data);
            adminSubscribersRef.current.forEach((cb) => cb(msg.data));
          }
        } catch (e) {
          console.warn("WS Parse Error:", e);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        // Exponential backoff reconnect
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 4000);
      };

      ws.onerror = () => {
        setIsConnected(false);
      };
    } catch (err) {
      console.warn("WebSocket init error:", err);
    }
  }, [user]);

  // Initial connection
  useEffect(() => {
    connectWebSocket();

    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [connectWebSocket]);

  // Track page transitions in real-time
  useEffect(() => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: "page_view",
        path: location.pathname + location.search,
        title: document.title,
        referrer: document.referrer
      }));
    }
  }, [location]);

  // Re-identify when auth state changes
  useEffect(() => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      if (user) {
        socketRef.current.send(JSON.stringify({
          type: "identify",
          user: {
            user_id: user.user_id,
            name: user.name,
            email: user.email,
            role: user.role
          }
        }));

        if (user.role === "admin") {
          socketRef.current.send(JSON.stringify({
            type: "admin:subscribe",
            token: "admin_active_session"
          }));
        }
      }
    }
  }, [user]);

  const dismissNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const subscribeAdminLiveStats = (callback) => {
    adminSubscribersRef.current.add(callback);
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: "admin:subscribe",
        token: "admin_active_session"
      }));
    }
    return () => {
      adminSubscribersRef.current.delete(callback);
    };
  };

  const sendBroadcastNotification = async (payload) => {
    const { data } = await api.post("/admin/live/broadcast", payload);
    return data;
  };

  return (
    <LiveAnalyticsContext.Provider
      value={{
        isConnected,
        notifications,
        dismissNotification,
        adminLiveStats,
        subscribeAdminLiveStats,
        sendBroadcastNotification,
        playChime: playNotificationChime
      }}
    >
      {children}
    </LiveAnalyticsContext.Provider>
  );
}

export function useLiveAnalytics() {
  const context = useContext(LiveAnalyticsContext);
  if (!context) {
    throw new Error("useLiveAnalytics must be used within a LiveAnalyticsProvider");
  }
  return context;
}
